import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { ExternalLink, Link2Off, Loader2, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from '@apm/contracts';
import { Button } from '../components/ui/button';
import { useAuth } from '../features/auth/auth-context';
import { MiniAppHome } from '../features/telegram/components/mini-app-home';
import { errorMessage } from '../lib/error-message';
import { ApiRequestError } from '../lib/http';
import {
  closeMiniApp,
  initDataOf,
  onActivated,
  openExternal,
  TELEGRAM_LINK_PATH,
  telegramReady,
} from '../features/telegram/webapp';

/**
 * Mini App Telegram — APM-TG-i, docs/plan-telegram.md.
 *
 * ## Vì sao nằm ngoài `_app`
 *
 * Cùng lý do và cùng tiền lệ với `/portal`: `AppShell` mang menu điều hướng,
 * `QuickUpdateFab` và `GuidedTour` — cả ba đều vô nghĩa trên một màn hình cao
 * 600px trong webview của Telegram, và hai trong ba chiếm mất chỗ của thứ người
 * dùng mở Mini App để xem.
 *
 * ## Đăng nhập không có màn hình đăng nhập
 *
 * `initData` mà Telegram cấp *là* thông tin xác thực. Người dùng đã đăng nhập ở
 * Telegram rồi; bắt họ gõ mật khẩu lần nữa trong một webview là bỏ đi lý do tồn
 * tại của Mini App.
 *
 * ## Chưa kết nối: đi kết nối ở trình duyệt ngoài, rồi tự quay về — APM-TG-n
 *
 * Kết nối cần một phiên web, mà webview của Telegram thì không có cookie của
 * trình duyệt: một link thường mở ngay trong nó là bắt người dùng đăng nhập
 * Google lại từ đầu. Nên nút kết nối mở `/settings?section=telegram` bằng **trình
 * duyệt ngoài** (`openExternal`), nơi họ gần như luôn đã đăng nhập, và Mini App
 * tự thử lại khi được đưa lên trước lần nữa.
 *
 * ## ⚠️ Mở bằng trình duyệt thường phải nói ra, không được là màn hình trắng
 *
 * Script của Telegram nạp được ở mọi trang, nên `window.Telegram.WebApp` tồn
 * tại cả trong Chrome — chỉ `initData` là rỗng. Đó là trạng thái mà dev gặp
 * suốt, và người dùng cũng gặp khi họ dán link cho nhau.
 */
export const Route = createFileRoute('/tg')({ component: MiniApp });

const SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js?57';

/** Nạp script của Telegram **một lần**, kể cả khi StrictMode gắn effect hai lần. */
let scriptLoad: Promise<void> | null = null;

function loadTelegramScript(): Promise<void> {
  scriptLoad ??= new Promise<void>((resolve) => {
    if (window.Telegram?.WebApp) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    // `resolve` cả khi hỏng: không nạp được script là một trạng thái phải hiện
    // ra màn hình ("chỉ chạy trong Telegram"), không phải một promise treo mãi.
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
  return scriptLoad;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'outside' }
  | { kind: 'ready' }
  | { kind: 'notLinked' }
  | { kind: 'error'; message: string };

function MiniApp(): ReactNode {
  const { t } = useTranslation();
  const { status, loginWithTelegram } = useAuth();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  /** Tăng lên để chạy lại lượt đăng nhập — sau khi người dùng kết nối xong ở web. */
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setPhase({ kind: 'loading' });
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      await loadTelegramScript();
      if (cancelled) return;

      telegramReady();
      const initData = initDataOf();
      if (!initData) {
        setPhase({ kind: 'outside' });
        return;
      }

      // Đã có phiên (mở lại Mini App trong cùng một tab) thì không đổi vé nữa:
      // `adopt` khi đang đăng nhập sẽ **nạp lại trang**, và nạp lại giữa lúc
      // khởi động là một vòng lặp.
      if (status === 'authenticated') {
        setPhase({ kind: 'ready' });
        return;
      }
      if (status === 'restoring') return;

      try {
        await loginWithTelegram(initData);
        if (!cancelled) setPhase({ kind: 'ready' });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiRequestError && error.code === ErrorCode.TELEGRAM_NOT_LINKED) {
          setPhase({ kind: 'notLinked' });
          return;
        }
        setPhase({ kind: 'error', message: errorMessage(error, t) });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, loginWithTelegram, t, attempt]);

  // Người dùng vừa kết nối ở trình duyệt ngoài rồi quay lại Telegram: thử lại
  // ngay, khỏi bắt họ tìm nút. Chỉ ở trạng thái chưa kết nối — ở các trạng thái
  // khác, tự chạy lại mỗi lần app lên trước là gọi API vô ích.
  useEffect(() => {
    if (phase.kind !== 'notLinked') return undefined;
    return onActivated(retry);
  }, [phase.kind, retry]);

  if (phase.kind === 'loading') {
    return (
      <Centered>
        <Loader2 aria-hidden className="mb-1 size-5 animate-spin text-primary" />
        {t('miniApp.loading')}
      </Centered>
    );
  }

  if (phase.kind === 'outside') {
    return (
      <Centered>
        <ExternalLink aria-hidden className="mb-1 size-5 text-primary" />
        <p>{t('miniApp.outside')}</p>
        <a className="mt-2 text-primary underline" href="/">
          {t('miniApp.openWeb')}
        </a>
      </Centered>
    );
  }

  if (phase.kind === 'notLinked') {
    return (
      <Centered>
        <Link2Off aria-hidden className="mb-1 size-5 text-rag-amber" />
        <p className="font-medium text-foreground">{t('miniApp.notLinked.title')}</p>
        <p>{t('miniApp.notLinked.help')}</p>
        <Button
          type="button"
          className="mt-3 w-full"
          onClick={() => openExternal(TELEGRAM_LINK_PATH)}
        >
          {t('miniApp.notLinked.openBrowser')}
        </Button>
        <Button type="button" variant="outline" className="mt-1 w-full" onClick={retry}>
          {t('miniApp.notLinked.retry')}
        </Button>
      </Centered>
    );
  }

  if (phase.kind === 'error') {
    return <MiniAppError message={phase.message} />;
  }

  return <MiniAppHome />;
}

/**
 * Mọi lỗi khác "chưa kết nối" — APM-TG-o.
 *
 * Lỗi `initData` (chữ ký sai, quá 15 phút) về cùng một mã, và cách chữa chung là
 * mở lại: Telegram cấp `initData` mới ở mỗi lần mở. Nhưng:
 *
 * - **Luôn có đường sang web.** Ngày 2026-09-25 lỗi chữ ký chặn **mọi** lượt mở,
 *   và màn hình chỉ có một nút "Đóng và mở lại" — mở lại thì lỗi y hệt, và không
 *   có cách nào khác để vào sản phẩm. Trình duyệt ngoài thì luôn dùng được.
 * - **`close()` có thể không làm gì.** Vài client (Telegram Desktop cũ, webview
 *   nhúng) bỏ qua nó. Bấm mà màn hình vẫn đứng yên thì nói cho người dùng tự
 *   đóng bằng dấu ×, thay vì một cái nút trông như hỏng.
 */
function MiniAppError({ message }: { message: string }): ReactNode {
  const { t } = useTranslation();
  const [closeTried, setCloseTried] = useState(false);

  return (
    <Centered>
      <TriangleAlert aria-hidden className="mb-1 size-5 text-rag-red" />
      <p className="font-medium text-foreground">{t('miniApp.error.title')}</p>
      <p>{message}</p>
      <Button type="button" className="mt-3 w-full" onClick={() => openExternal('/')}>
        {t('miniApp.error.openBrowser')}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="mt-1 w-full"
        onClick={() => {
          closeMiniApp();
          // Còn chạy được tới đây nghĩa là cửa sổ chưa đóng — hoặc sắp đóng. Hiện
          // lời nhắn ngay cũng không sao: nếu nó đóng thật thì chẳng ai thấy.
          setCloseTried(true);
        }}
      >
        {t('miniApp.reopen')}
      </Button>
      {closeTried ? <p className="mt-2 text-xs">{t('miniApp.error.closeManually')}</p> : null}
    </Centered>
  );
}

/**
 * Màn trạng thái (đang mở · ngoài Telegram · chưa kết nối · lỗi): một thẻ kính
 * giữa nền ambient — APM-TG-glass. Nút bên trong là `Button` chung, tự thành
 * viên kính.
 */
function Centered({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="glass-ambient flex min-h-dvh items-center justify-center p-5">
      <div className="glass-panel flex w-full max-w-sm flex-col items-center gap-1.5 rounded-3xl px-5 py-6 text-center text-sm text-muted-foreground">
        {children}
      </div>
    </div>
  );
}
